// src/dataTypes/broadcastingManagment/scheduleOperations.ts
//
// Opérations de programmation — remplace l'ancienne fonction recalc() de
// BroadcastedMediaListV2, qui appliquait UNE seule sémantique ("recoller tous les
// programmes bout à bout à partir du premier") aux quatre opérations de l'écran.
//
// Modèle retenu : un trou volontaire entre deux programmes est un PSEUDO-PROGRAMME.
// Il occupe une position dans la séquence et doit survivre à toutes les opérations.
// L'écart avant le programme de rang i vaut : début(i) - fin(i-1).
//
//   • Réorganiser (glisser-déposer ET flèches) — les trous restent à LEUR position dans
//     la séquence ; seuls les programmes sont permutés, puis les heures sont recalculées
//     en reparcourant la séquence. Conséquence utile : permuter deux programmes voisins
//     ne déplace rien avant ni après eux, puisque la durée totale du couple (durées +
//     trou intermédiaire) est inchangée.
//
//   • Supprimer — les programmes suivants avancent d'un delta UNIFORME égal à la durée
//     du supprimé ; les écarts entre eux sont donc préservés, et les deux trous qui
//     encadraient le supprimé fusionnent. C'est exactement la stratégie 'shift_all' du
//     scheduler ffmpeg (_apply_fill_strategy), ce qui aligne enfin le front sur lui.
//
//   • Annuler un ajout — un ajout ne décale rien (cf. handleAddConfirm), son annulation
//     ne décale donc rien non plus : simple retrait de la liste.
//
// Toutes les fonctions sont PURES : elles ne mutent jamais la liste ni les éléments
// reçus, et renvoient la liste d'origine à l'identique (même référence) quand il n'y a
// rien à faire. Testées dans scheduleOperations.test.ts.

import { TaggedEnrichedBroadcastedMedia } from './types';

const MS = 1000;

// Programmes dont l'heure est figée : déjà diffusés, en cours de diffusion, ou sortis du
// planning. Ils ne sont jamais déplacés ni recalculés, et n'entrent pas dans le chaînage.
// (Même liste que l'ancien isPermutable de BroadcastedMediaListV2.)
const LOCKED_TAGS = new Set([
  'to archive', 'archived', 'current', 'missed', 'being missed', 'not found',
]);

// Tags qui ne doivent jamais être écrasés par "to update" : soit l'élément a déjà un
// état en attente plus fort (création/suppression), soit il est figé.
const NON_UPDATABLE_TAGS = new Set(['to delete', 'to create', 'to archive', 'current']);

/** Un programme que l'utilisateur peut réorganiser (glisser-déposer, flèches).
 *  Un programme prioritaire (« Lire maintenant ») n'en est pas un : il se pose PAR-DESSUS
 *  la grille à l'heure où il a été lancé, il n'occupe pas de place dans la séquence. */
export const isReorderable = (m: TaggedEnrichedBroadcastedMedia): boolean =>
  !LOCKED_TAGS.has(m.tag) && !m.priority;

/** Un programme en attente de suppression : il n'occupe plus de temps dans la grille. */
export const isPendingDeletion = (m: TaggedEnrichedBroadcastedMedia): boolean =>
  m.tag === 'to delete';

/** Un programme qui occupe réellement du temps et dont l'heure peut être recalculée. */
export const isActiveProgram = (m: TaggedEnrichedBroadcastedMedia): boolean =>
  !LOCKED_TAGS.has(m.tag) && !isPendingDeletion(m) && !m.priority;

const durationSeconds = (m: TaggedEnrichedBroadcastedMedia): number =>
  typeof m.duration === 'number' && Number.isFinite(m.duration) ? m.duration : 0;

const startMs = (m: TaggedEnrichedBroadcastedMedia): number =>
  m.broadcastDate ? new Date(m.broadcastDate).getTime() : NaN;

const endMs = (m: TaggedEnrichedBroadcastedMedia): number =>
  startMs(m) + durationSeconds(m) * MS;

/**
 * Marque un programme comme modifié pour qu'il soit poussé au serveur.
 * `oldTag` n'est renseigné qu'au premier marquage, sinon un second passage écraserait
 * le tag d'origine par "to update" et l'annulation ne saurait plus quoi restaurer.
 */
const withUpdatedTag = (m: TaggedEnrichedBroadcastedMedia): TaggedEnrichedBroadcastedMedia => {
  if (NON_UPDATABLE_TAGS.has(m.tag) || m.tag === 'to update') return m;
  return { ...m, oldTag: m.tag, tag: 'to update' };
};

/**
 * Applique les nouvelles heures de début (indexées par position dans la liste) et marque
 * "to update" les seuls programmes dont l'heure a RÉELLEMENT changé — l'ancien code
 * marquait tout ce qui suivait l'indice modifié, y compris des programmes inchangés, et
 * inversement recalculait des heures qu'il ne marquait pas (divergence silencieuse entre
 * ce qui est affiché et ce qui est en base).
 */
const applyStarts = (
  list: TaggedEnrichedBroadcastedMedia[],
  newStarts: Map<number, number>,
): TaggedEnrichedBroadcastedMedia[] => {
  let changed = false;

  const next = list.map((m, i) => {
    const target = newStarts.get(i);
    if (target === undefined || !Number.isFinite(target)) return m;
    if (startMs(m) === target) return m;
    changed = true;
    return withUpdatedTag({ ...m, broadcastDate: new Date(target) });
  });

  return changed ? next : list;
};

/**
 * Décale d'un delta uniforme tous les programmes actifs situés APRÈS `afterIndex`.
 * Un delta uniforme préserve mécaniquement les écarts entre eux — c'est la propriété
 * qui fait survivre les trous volontaires.
 */
const shiftActiveAfter = (
  list: TaggedEnrichedBroadcastedMedia[],
  afterIndex: number,
  deltaMs: number,
): TaggedEnrichedBroadcastedMedia[] => {
  if (deltaMs === 0) return list;

  const newStarts = new Map<number, number>();

  for (let i = afterIndex + 1; i < list.length; i++) {
    const m = list[i];
    if (!isActiveProgram(m)) continue;
    const start = startMs(m);
    if (!Number.isFinite(start)) continue;
    newStarts.set(i, start + deltaMs);
  }

  return applyStarts(list, newStarts);
};

/**
 * Déplace un programme de `srcIndex` vers `destIndex` et recalcule les heures en
 * reparcourant la séquence, les écarts restant à leur position.
 *
 * Couvre aussi bien le glisser-déposer que les flèches Monter/Descendre : permuter deux
 * voisins revient à déplacer l'un d'un cran, et le recalcul rend alors les programmes
 * situés avant et après le couple strictement inchangés.
 */
export const reorderProgram = (
  list: TaggedEnrichedBroadcastedMedia[],
  srcIndex: number,
  destIndex: number,
): TaggedEnrichedBroadcastedMedia[] => {

  if (srcIndex < 0 || srcIndex >= list.length) return list;

  const dest = Math.max(0, Math.min(list.length - 1, destIndex));
  if (dest === srcIndex) return list;

  // 1. Écarts positionnels de la séquence active, mesurés AVANT le déplacement.
  const activeBefore = list.filter(isActiveProgram);

  const gaps = activeBefore.map((m, rank) =>
    rank === 0 ? 0 : startMs(m) - endMs(activeBefore[rank - 1]));

  const firstStart = activeBefore.length > 0 ? startMs(activeBefore[0]) : NaN;

  // 2. Déplacement dans la liste complète.
  const moved = [...list];
  const [item] = moved.splice(srcIndex, 1);
  moved.splice(dest, 0, item);

  // Sans séquence active exploitable (liste vide, uniquement des programmes figés, ou
  // date manquante sur le premier), on se contente de réordonner sans toucher aux heures.
  if (activeBefore.length === 0 || !Number.isFinite(firstStart)) return moved;

  // 3. Reparcours : le programme de rang i démarre à la fin du précédent + l'écart qui
  //    occupait déjà cette position dans la séquence.
  const newStarts = new Map<number, number>();
  let cursor = firstStart;
  let previousDurationMs = 0;
  let rank = 0;

  for (let i = 0; i < moved.length; i++) {
    const m = moved[i];
    if (!isActiveProgram(m)) continue;

    if (rank > 0) {
      const gap = gaps[rank];
      cursor = cursor + previousDurationMs + (Number.isFinite(gap) ? gap : 0);
    }

    newStarts.set(i, cursor);
    previousDurationMs = durationSeconds(m) * MS;
    rank++;
  }

  return applyStarts(moved, newStarts);
};

/**
 * Marque un programme "à supprimer" et comble le trou : les programmes actifs suivants
 * avancent de sa durée, écarts préservés. Le programme marqué conserve son heure (il
 * reste affiché barré tant que la mise à jour n'est pas envoyée, et reste annulable).
 */
export const deleteProgram = (
  list: TaggedEnrichedBroadcastedMedia[],
  index: number,
): TaggedEnrichedBroadcastedMedia[] => {

  const target = list[index];
  if (!target || isPendingDeletion(target)) return list;

  const tagged = list.map((m, i) =>
    i === index ? { ...m, oldTag: m.tag, tag: 'to delete' } : m);

  return shiftActiveAfter(tagged, index, -durationSeconds(target) * MS);
};

/** Annule une suppression : opération strictement inverse de deleteProgram. */
export const restoreProgram = (
  list: TaggedEnrichedBroadcastedMedia[],
  index: number,
): TaggedEnrichedBroadcastedMedia[] => {

  const target = list[index];
  if (!target || !isPendingDeletion(target)) return list;

  const restored = list.map((m, i) =>
    i === index ? { ...m, tag: m.oldTag, oldTag: '' } : m);

  return shiftActiveAfter(restored, index, durationSeconds(target) * MS);
};

/**
 * Annule l'ajout d'un programme pas encore enregistré. Un ajout ne décale jamais les
 * autres programmes (il se pose à l'heure choisie, ou à la suite du dernier) : son
 * annulation ne doit donc rien décaler non plus.
 */
export const cancelNewProgram = (
  list: TaggedEnrichedBroadcastedMedia[],
  index: number,
): TaggedEnrichedBroadcastedMedia[] => {

  if (index < 0 || index >= list.length) return list;

  const next = [...list];
  next.splice(index, 1);
  return next;
};

// ── « Jouer en boucle » ────────────────────────────────────────────────────────
//
// Un programme en boucle n'a pas de fin : ceux qui le suivent sont RETENUS (affichés
// grisés) — le microservice ne les lance pas tant que la boucle tient l'antenne. Ils
// gardent leurs heures, sans signification pendant la boucle. À la désactivation, ils
// repartent à la fin du tour en cours : décalage UNIFORME (écarts préservés, comme pour
// une suppression) de « fin du tour − fin d'un passage unique ».
//
// Comme deleteProgram, ces fonctions supposent la liste triée chronologiquement.

// Programmes qui ne joueront plus : leur boucle ne retient rien, et eux ne sont pas retenus.
const ENDED_TAGS = new Set([
  'to delete', 'to archive', 'archived', 'missed', 'being missed', 'not found',
]);

/** Index du programme en boucle qui retient la suite (le premier armé), ou -1. */
export const loopHolderIndex = (list: TaggedEnrichedBroadcastedMedia[]): number =>
  list.findIndex(m => m.loop && !ENDED_TAGS.has(m.tag));

/** Programme retenu derrière une boucle : il ne partira qu'après sa désactivation. */
export const isHeldByLoop = (list: TaggedEnrichedBroadcastedMedia[], index: number): boolean => {
  const holder = loopHolderIndex(list);
  return holder !== -1 && index > holder && !!list[index] && !ENDED_TAGS.has(list[index].tag);
};

/**
 * Arme ou désarme la boucle du programme `index`.
 *
 * Désarmer un programme EN COURS : `cycleEndMs` = fin du tour en cours ; les programmes
 * suivants y sont recalés, et cette fin est retenue (loopCycleEnd) pour pouvoir annuler
 * le recalage si on réarme avant d'enregistrer. Pour un programme pas encore commencé,
 * pas de `cycleEndMs` : son premier passage finit à son heure naturelle, rien ne bouge.
 *
 * `loopDirty` bascule à chaque appel : réarmer après avoir désarmé revient à l'état
 * enregistré, sans rien envoyer.
 */
export const setProgramLoop = (
  list: TaggedEnrichedBroadcastedMedia[],
  index: number,
  loop: boolean,
  cycleEndMs: number = NaN,
): TaggedEnrichedBroadcastedMedia[] => {

  const target = list[index];
  if (!target || target.loop === loop) return list;

  const naturalEnd = endMs(target);
  let deltaMs = 0;
  let loopCycleEnd: Date | null = null;

  if (!loop && Number.isFinite(cycleEndMs) && Number.isFinite(naturalEnd) && cycleEndMs > naturalEnd) {
    deltaMs      = cycleEndMs - naturalEnd;
    loopCycleEnd = new Date(cycleEndMs);
  } else if (loop && target.loopCycleEnd) {
    deltaMs = naturalEnd - new Date(target.loopCycleEnd).getTime();
  }

  const toggled = list.map((m, i) =>
    i === index ? { ...m, loop, loopDirty: !m.loopDirty, loopCycleEnd } : m);

  return shiftActiveAfter(toggled, index, deltaMs);
};

/**
 * À l'enregistrement d'une désactivation : recale les programmes suivants sur la fin
 * EXACTE du tour, renvoyée par le microservice — celle retenue par setProgramLoop a pu
 * vieillir (un tour de plus s'est écoulé avant l'enregistrement).
 */
export const correctLoopRelease = (
  list: TaggedEnrichedBroadcastedMedia[],
  index: number,
  exactCycleEndMs: number,
): TaggedEnrichedBroadcastedMedia[] => {

  const target = list[index];
  if (!target || target.loop || !Number.isFinite(exactCycleEndMs)) return list;

  const assumed = target.loopCycleEnd ? new Date(target.loopCycleEnd).getTime() : endMs(target);
  if (!Number.isFinite(assumed)) return list;

  const deltaMs = exactCycleEndMs - assumed;
  if (Math.abs(deltaMs) < MS) return list;

  const updated = list.map((m, i) => i === index ? { ...m, loopCycleEnd: new Date(exactCycleEndMs) } : m);
  return shiftActiveAfter(updated, index, deltaMs);
};

/** Part d'un programme recouverte par un programme prioritaire (« Lire maintenant »).
 *  `from`/`to` sont des fractions de sa durée (0 = début, 1 = fin) : le voile de
 *  BroadcastedMediaListV2 en tire sa hauteur. Le prioritaire ne coupe que ce qui joue ou
 *  jouera pendant qu'il passe ; un programme supprimé, introuvable ou lui-même prioritaire
 *  n'est jamais voilé. Plusieurs prioritaires : l'étendue qu'ils couvrent. */
export interface PriorityCover { from: number; to: number; by: TaggedEnrichedBroadcastedMedia }

// Une archive reste voilée : le bloc d'un prioritaire déjà joué doit rester lisible.
const NEVER_COVERED_TAGS = new Set(['to delete', 'not found']);

export const priorityCover = (
  list: TaggedEnrichedBroadcastedMedia[],
  index: number,
): PriorityCover | null => {
  const m = list[index];
  if (!m || m.priority || NEVER_COVERED_TAGS.has(m.tag)) return null;

  const start = startMs(m);
  const dur   = durationSeconds(m) * MS;
  if (!Number.isFinite(start) || dur <= 0) return null;
  const end   = start + dur;

  let cover: PriorityCover | null = null;
  for (const p of list) {
    // Un prioritaire déjà diffusé (archivé) garde son voile : il a réellement recouvert.
    if (!p.priority || p.tag === 'to delete' || p.tag === 'not found') continue;
    const ps = startMs(p);
    const pe = endMs(p);
    if (!Number.isFinite(ps) || pe <= start || ps >= end) continue;
    const from = (Math.max(ps, start) - start) / dur;
    const to   = (Math.min(pe, end)   - start) / dur;
    cover = cover
      ? { from: Math.min(cover.from, from), to: Math.max(cover.to, to), by: cover.by }
      : { from, to, by: p };
  }
  return cover;
};

/**
 * Fin réelle d'un programme : celle du tour en cours pour une boucle tout juste désarmée
 * (pas encore enregistrée), sinon début + durée. Une fois la désactivation enregistrée,
 * admin-api a ramené broadcastDate au début du dernier tour et les deux coïncident.
 */
export const effectiveEndMs = (m: TaggedEnrichedBroadcastedMedia): number =>
  m.loopCycleEnd ? new Date(m.loopCycleEnd).getTime() : endMs(m);

// Ne peuvent pas être « en cours » : sortis du planning, ratés, ou pas encore envoyés
// (un ajout local ne joue qu'après « Mettre à jour le programme »).
const NEVER_LIVE_TAGS = new Set([
  'to create', 'to delete', 'to archive', 'archived', 'missed', 'being missed', 'not found',
]);

/** Indice du programme à l'antenne à l'instant `nowMs`, d'après les HEURES de la grille, ou -1.
 *  Le statut 'current' d'admin-api arrive tard (webhook video_started retardé de 7 s, puis
 *  sondage toutes les 15 s) : un programme dont l'heure est atteinte restait affiché « à venir »
 *  jusqu'à ~20 s après l'enregistrement. Un prioritaire dans sa fenêtre l'emporte toujours sur
 *  ce qu'il recouvre (il l'a coupé) ; à défaut, le dernier commencé dont la fenêtre est ouverte. */
export const liveProgramIndex = (
  list: TaggedEnrichedBroadcastedMedia[],
  nowMs: number,
): number => {
  let best = -1;
  list.forEach((m, i) => {
    if (NEVER_LIVE_TAGS.has(m.tag)) return;
    const start = startMs(m);
    if (!Number.isFinite(start) || start > nowMs) return;
    if (!m.loop && endMs(m) <= nowMs) return;
    if (best === -1) { best = i; return; }
    const b = list[best];
    if (m.priority !== b.priority) { if (m.priority) best = i; return; }
    if (start >= startMs(b)) best = i;
  });
  return best;
};

/** Archives du jour à garder dans la Programmation : les prioritaires (« Lire maintenant »)
 *  et ce que leur fenêtre a recouvert. Les autres archives (diffusions ordinaires) n'y ont
 *  jamais figuré et en restent dehors. Les programmes vivants passent tous. */
export const keepPriorityArchives = <T extends {
  archived?: boolean; priority: boolean; broadcastDate: Date | null; duration: number;
}>(list: T[]): T[] => {
  const start = (m: T) => m.broadcastDate ? new Date(m.broadcastDate).getTime() : NaN;
  const end   = (m: T) => start(m) + (Number.isFinite(m.duration) ? m.duration : 0) * MS;
  const windows = list.filter(m => m.priority).map(p => [start(p), end(p)] as const);
  return list.filter(m => {
    if (!m.archived || m.priority) return true;
    const s = start(m), e = end(m);
    return windows.some(([ps, pe]) => Number.isFinite(ps) && ps < e && pe > s);
  });
};

/** Le programme a fini de jouer à l'instant `nowMs`, d'après les HEURES de la grille. Sert à
 *  l'affichage « À archiver » dès la fin, sans attendre le webhook video_completed (relayé par le
 *  sondage toutes les 15 s). Une boucle ne finit jamais ; un ajout ou une suppression pas encore
 *  envoyés ne sont pas concernés. */
export const hasEnded = (m: TaggedEnrichedBroadcastedMedia, nowMs: number): boolean =>
  !m.loop && m.tag !== 'to create' && m.tag !== 'to delete'
  && Number.isFinite(startMs(m)) && effectiveEndMs(m) <= nowMs;

